import React, { useState, useEffect } from 'react';
import { Bell, Save, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { notificationService, type UserPreferences, type UpdatePreferencesRequest } from '../services/notificationService';
import { useToast } from '../components/Toast';
import { useI18n } from '../contexts/I18nContext';

const PreferenciasNotificacoes: React.FC = () => {
  const { t } = useI18n();
  const { showSuccess, showError } = useToast();

  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await notificationService.getPreferences();
      setPreferences(data);
    } catch (err: any) {
      console.error('Erro ao carregar preferências:', err);
      setError(err.response?.data?.error || 'Erro ao carregar preferências');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!preferences) return;

    try {
      setSaving(true);
      setError(null);

      const updateData: UpdatePreferencesRequest = {
        channels: preferences.channels,
        quiet_hours_start: preferences.quiet_hours_start || null,
        quiet_hours_end: preferences.quiet_hours_end || null,
        appointment_reminders: preferences.appointment_reminders,
        appointment_created: preferences.appointment_created,
        appointment_cancelled: preferences.appointment_cancelled,
        payment_notifications: preferences.payment_notifications,
        system_notifications: preferences.system_notifications,
      };

      const updated = await notificationService.updatePreferences(updateData);
      setPreferences(updated);
      showSuccess('Preferências salvas com sucesso!');
    } catch (err: any) {
      console.error('Erro ao salvar preferências:', err);
      const errorMsg = err.response?.data?.error || 'Erro ao salvar preferências';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const updateChannel = (channel: keyof UserPreferences['channels'], value: boolean) => {
    if (!preferences) return;
    setPreferences({
      ...preferences,
      channels: {
        ...preferences.channels,
        [channel]: value,
      },
    });
  };

  const updatePreference = (key: keyof UserPreferences, value: boolean | string | null) => {
    if (!preferences) return;
    setPreferences({
      ...preferences,
      [key]: value,
    });
  };

  const formatTime = (timeStr: string | null | undefined): string => {
    if (!timeStr) return '';
    // Se já está no formato HH:MM, retornar
    if (timeStr.match(/^\d{2}:\d{2}$/)) return timeStr;
    // Se for um time do banco, extrair apenas HH:MM
    if (timeStr.includes('T')) {
      return timeStr.split('T')[1]?.substring(0, 5) || '';
    }
    return '';
  };

  if (loading) {
    return (
      <div style={{ padding: '30px', textAlign: 'center' }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '16px' }} />
        <div style={{ color: '#6b7280' }}>{t('preferences.loading')}</div>
      </div>
    );
  }

  if (!preferences) {
    return (
      <div style={{ padding: '30px', textAlign: 'center' }}>
        <AlertCircle size={48} color="#ef4444" style={{ marginBottom: '16px' }} />
        <div style={{ color: '#6b7280', fontSize: '16px' }}>
          {error || 'Não foi possível carregar as preferências'}
        </div>
        <button
          onClick={loadPreferences}
          style={{
            marginTop: '16px',
            padding: '10px 20px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >{t('billing.retryLoad')}</button>
      </div>
    );
  }

  return (
    <div style={{ padding: '30px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '600', color: '#1f2937', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Bell size={28} />
          Preferências de Notificação
        </h1>
        <p style={{ color: '#6b7280', fontSize: '14px' }}>{t('preferences.subtitle')}</p>
      </div>

      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#991b1b',
          padding: '12px 16px',
          borderRadius: '6px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Canais de Notificação */}
      <div style={{
        background: 'white',
        padding: '24px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '24px',
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '20px' }}>{t('preferences.channels')}</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <label htmlFor="notification-channel-web" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
            <div>
              <div style={{ fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Notificações Web</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Receber notificações no navegador</div>
            </div>
            <input
              id="notification-channel-web"
              name="notification-channel-web"
              type="checkbox"
              checked={preferences.channels?.web ?? true}
              onChange={(e) => updateChannel('web', e.target.checked)}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
          </label>

          <label htmlFor="notification-channel-email" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
            <div>
              <div style={{ fontWeight: '500', color: '#374151', marginBottom: '4px' }}>{t('common.email')}</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Receber notificações por email</div>
            </div>
            <input
              id="notification-channel-email"
              name="notification-channel-email"
              type="checkbox"
              checked={preferences.channels?.email ?? true}
              onChange={(e) => updateChannel('email', e.target.checked)}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
          </label>

          <label htmlFor="notification-channel-sms" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
            <div>
              <div style={{ fontWeight: '500', color: '#374151', marginBottom: '4px' }}>SMS</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Receber notificações por SMS</div>
            </div>
            <input
              id="notification-channel-sms"
              name="notification-channel-sms"
              type="checkbox"
              checked={preferences.channels?.sms ?? false}
              onChange={(e) => updateChannel('sms', e.target.checked)}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
          </label>

          <label htmlFor="notification-channel-push-mobile" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
            <div>
              <div style={{ fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Push Mobile</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Receber notificações push no app móvel</div>
            </div>
            <input
              id="notification-channel-push-mobile"
              name="notification-channel-push-mobile"
              type="checkbox"
              checked={preferences.channels?.push_mobile ?? true}
              onChange={(e) => updateChannel('push_mobile', e.target.checked)}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
          </label>
        </div>
      </div>

      {/* Horários Silenciosos */}
      <div style={{
        background: 'white',
        padding: '24px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '24px',
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '20px' }}>{t('preferences.quietHours')}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label htmlFor="quiet-hours-start" style={{ display: 'block', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>{t('preferences.quietStart')}</label>
            <input
              id="quiet-hours-start"
              name="quiet-hours-start"
              type="time"
              value={formatTime(preferences.quiet_hours_start)}
              onChange={(e) => updatePreference('quiet_hours_start', e.target.value || null)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>
          <div>
            <label htmlFor="quiet-hours-end" style={{ display: 'block', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
              Fim (HH:MM)
            </label>
            <input
              id="quiet-hours-end"
              name="quiet-hours-end"
              type="time"
              value={formatTime(preferences.quiet_hours_end)}
              onChange={(e) => updatePreference('quiet_hours_end', e.target.value || null)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>
        </div>
        <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '12px' }}>{t('preferences.quietHoursHint')}</p>
      </div>

      {/* Tipos de Notificação */}
      <div style={{
        background: 'white',
        padding: '24px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '24px',
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '20px' }}>
          Tipos de Notificação
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <label htmlFor="notification-type-appointment-reminders" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
            <div>
              <div style={{ fontWeight: '500', color: '#374151', marginBottom: '4px' }}>{t('preferences.appointmentReminders')}</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Receber lembretes antes dos agendamentos</div>
            </div>
            <input
              id="notification-type-appointment-reminders"
              name="notification-type-appointment-reminders"
              type="checkbox"
              checked={preferences.appointment_reminders}
              onChange={(e) => updatePreference('appointment_reminders', e.target.checked)}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
          </label>

          <label htmlFor="notification-type-appointment-created" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
            <div>
              <div style={{ fontWeight: '500', color: '#374151', marginBottom: '4px' }}>{t('preferences.appointmentCreated')}</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Notificar quando um agendamento for criado</div>
            </div>
            <input
              id="notification-type-appointment-created"
              name="notification-type-appointment-created"
              type="checkbox"
              checked={preferences.appointment_created}
              onChange={(e) => updatePreference('appointment_created', e.target.checked)}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
          </label>

          <label htmlFor="notification-type-appointment-cancelled" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
            <div>
              <div style={{ fontWeight: '500', color: '#374151', marginBottom: '4px' }}>{t('preferences.appointmentCancelled')}</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Notificar quando um agendamento for cancelado</div>
            </div>
            <input
              id="notification-type-appointment-cancelled"
              name="notification-type-appointment-cancelled"
              type="checkbox"
              checked={preferences.appointment_cancelled}
              onChange={(e) => updatePreference('appointment_cancelled', e.target.checked)}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
          </label>

          <label htmlFor="notification-type-payment" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
            <div>
              <div style={{ fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Notificações de Pagamento</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Receber notificações sobre pagamentos</div>
            </div>
            <input
              id="notification-type-payment"
              name="notification-type-payment"
              type="checkbox"
              checked={preferences.payment_notifications}
              onChange={(e) => updatePreference('payment_notifications', e.target.checked)}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
          </label>

          <label htmlFor="notification-type-system" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
            <div>
              <div style={{ fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Notificações do Sistema</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Receber notificações gerais do sistema</div>
            </div>
            <input
              id="notification-type-system"
              name="notification-type-system"
              type="checkbox"
              checked={preferences.system_notifications}
              onChange={(e) => updatePreference('system_notifications', e.target.checked)}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
          </label>
        </div>
      </div>

      {/* Botão Salvar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '12px 24px',
            background: saving ? '#9ca3af' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {saving ? (
            <>
              <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
              Salvando...
            </>
          ) : (
            <>
              <Save size={18} />
              Salvar Preferências
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default PreferenciasNotificacoes;

